/**
 * AuditCalendar - Audit planning, scheduling, and calendar view
 * 
 * Features:
 *   - Calendar grid showing scheduled, in-progress, and completed audits
 *   - Recurring audit programmes (daily/weekly/monthly/quarterly/annual)
 *   - Drag-to-schedule (future), assignment management
 *   - Overdue indicators
 */

import React, { useState, useEffect } from 'react';
import {
  ChevronLeft, ChevronRight, Plus, Repeat, Loader2,
} from 'lucide-react';
import { Card, CardContent, CardHeader } from '@shared/components/ui/card';
import { Button } from '@shared/components/ui/button';
import { Badge } from '@shared/components/ui/badge';
import { Input } from '@shared/components/ui/input';
import { Label } from '@shared/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@shared/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@shared/components/ui/dialog';
import { toast } from '@shared/components/ui/use-toast';
import { supabase } from '@shared/supabaseClient';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const STATUS_COLORS = {
  scheduled:   'bg-blue-500',
  in_progress: 'bg-amber-500',
  completed:   'bg-green-500',
  overdue:     'bg-red-500',
  cancelled:   'bg-gray-400',
};

// Generate schedule dates for a recurring programme (up to 12 months or end_date)
function generateScheduleDates(frequency, startDate, endDate) {
  const dates = [];
  const start = new Date(startDate + 'T00:00:00');
  const maxEnd = endDate
    ? new Date(endDate + 'T23:59:59')
    : new Date(start.getFullYear() + 1, start.getMonth(), start.getDate()); // 12 months ahead
  const MAX_ENTRIES = 365; // safety cap

  let current = new Date(start);
  while (current <= maxEnd && dates.length < MAX_ENTRIES) {
    dates.push(current.toISOString().slice(0, 10));

    switch (frequency) {
      case 'daily':
        current.setDate(current.getDate() + 1);
        break;
      case 'weekly':
        current.setDate(current.getDate() + 7);
        break;
      case 'biweekly':
        current.setDate(current.getDate() + 14);
        break;
      case 'monthly':
        current.setMonth(current.getMonth() + 1);
        break;
      case 'quarterly':
        current.setMonth(current.getMonth() + 3);
        break;
      case 'biannual':
        current.setMonth(current.getMonth() + 6);
        break;
      case 'annual':
        current.setFullYear(current.getFullYear() + 1);
        break;
      default:
        current.setMonth(current.getMonth() + 1);
    }
  }
  return dates;
}

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year, month) {
  const day = new Date(year, month, 1).getDay();
  return day === 0 ? 6 : day - 1; // Mon=0, Sun=6
}

export default function AuditCalendar({ user }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [schedule, setSchedule] = useState([]);
  const [programmes, setProgrammes] = useState([]);
  const [audits, setAudits] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);
  const [showProgrammeDialog, setShowProgrammeDialog] = useState(false);
  const [templates, setTemplates] = useState([]);

  // Programme form 
  const [progForm, setProgForm] = useState({
    programme_name: '', template_id: '', frequency: 'monthly',
    start_date: '', end_date: '',
  });
  const [savingProg, setSavingProg] = useState(false);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadAllData(); }, [year, month]);

  const loadAllData = async () => {
    setIsLoading(true);
    try {
      const startOfMonth = new Date(year, month, 1).toISOString();
      const endOfMonth = new Date(year, month + 1, 0, 23, 59, 59).toISOString();

      const [scheduleRes, auditRes, progRes, templateRes] = await Promise.all([
        supabase.from('audit_schedule')
          .select('*, audit_programmes(programme_name)')
          .gte('due_date', startOfMonth)
          .lte('due_date', endOfMonth)
          .order('due_date'),
        supabase.from('audits')
          .select('id, audit_title, status, start_time, submitted_at, created_at')
          .gte('created_at', startOfMonth)
          .lte('created_at', endOfMonth),
        supabase.from('audit_programmes')
          .select('*')
          .eq('is_active', true)
          .order('programme_name'),
        supabase.from('audit_templates')
          .select('id, template_name')
          .eq('is_active', true)
          .order('template_name'),
      ]);

      setSchedule(scheduleRes.data || []);
      setAudits(auditRes.data || []);
      setProgrammes(progRes.data || []);
      setTemplates(templateRes.data || []);
    } catch (error) {
      console.error('Error loading calendar data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const d = new Date(prev);
      d.setMonth(d.getMonth() + direction);
      return d;
    });
  };

  const goToToday = () => setCurrentDate(new Date());

  // Get events for a specific day
  const getEventsForDay = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const scheduled = schedule.filter(s =>
      s.due_date && s.due_date.startsWith(dateStr)
    );
    const dayAudits = audits.filter(a => {
      const d = (a.start_time || a.created_at || '').slice(0, 10);
      return d === dateStr;
    });
    return { scheduled, audits: dayAudits };
  };

  const handleCreateProgramme = async () => {
    if (!progForm.programme_name || !progForm.template_id) {
      toast({ title: 'Required', description: 'Name and template are required', variant: 'destructive' });
      return;
    }
    if (!progForm.start_date) {
      toast({ title: 'Required', description: 'Start date is required', variant: 'destructive' });
      return;
    }
    try {
      setSavingProg(true);

      // Get the current published version of the template
      const { data: version } = await supabase
        .from('audit_template_versions')
        .select('id')
        .eq('template_id', progForm.template_id)
        .eq('status', 'published')
        .eq('is_current_version', true)
        .single();

      const { data: programme, error: progErr } = await supabase.from('audit_programmes').insert({
        programme_name: progForm.programme_name,
        template_id: progForm.template_id,
        frequency_type: progForm.frequency,
        next_due_date: progForm.start_date,
        depot_id: user.depot_id,
        created_by: user.id,
        is_active: true,
      }).select().single();

      if (progErr) throw progErr;

      // Auto-generate schedule entries for 12 months (or until end_date)
      const scheduleRows = generateScheduleDates(
        progForm.frequency,
        progForm.start_date,
        progForm.end_date || null,
      ).map(date => ({
        programme_id: programme.id,
        due_date: date,
        status: 'scheduled',
      }));

      if (scheduleRows.length > 0) {
        const { error: schedErr } = await supabase
          .from('audit_schedule')
          .insert(scheduleRows);
        if (schedErr) throw schedErr;
      }

      toast({
        title: 'Programme Created',
        description: `${progForm.programme_name} — ${scheduleRows.length} audit(s) scheduled`,
      });
      setShowProgrammeDialog(false);
      setProgForm({ programme_name: '', template_id: '', frequency: 'monthly', start_date: '', end_date: '' });
      loadAllData();
    } catch (error) {
      console.error('Error creating programme:', error);
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setSavingProg(false);
    }
  };

  // Calendar grid
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const today = new Date();
  const isToday = (day) => 
    day === today.getDate() && month === today.getMonth() && year === today.getFullYear();

  const calendarCells = [];
  // Empty cells for days before the 1st
  for (let i = 0; i < firstDay; i++) {
    calendarCells.push({ day: null, key: `empty-${i}` });
  }
  // Actual days
  for (let d = 1; d <= daysInMonth; d++) {
    calendarCells.push({ day: d, key: `day-${d}` });
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Audit Calendar</h2>
          <p className="text-sm text-muted-foreground">
            {programmes.length} active programme{programmes.length !== 1 ? 's' : ''} · {schedule.length} scheduled this month
          </p>
        </div>
        <Button onClick={() => setShowProgrammeDialog(true)}>
          <Plus className="h-4 w-4 mr-2" /> New Programme
        </Button>
      </div>

      {/* Active Programmes Summary */}
      {programmes.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {programmes.map(p => (
            <Badge key={p.id} variant="outline" className="text-xs py-1 px-2">
              <Repeat className="h-3 w-3 mr-1" />
              {p.programme_name}
              <span className="text-muted-foreground ml-1">({p.frequency})</span>
            </Badge>
          ))}
        </div>
      )}

      {/* Calendar */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" onClick={() => navigateMonth(-1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <h3 className="text-lg font-semibold min-w-[180px] text-center">
                {MONTHS[month]} {year}
              </h3>
              <Button variant="ghost" size="icon" onClick={() => navigateMonth(1)}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <Button variant="outline" size="sm" onClick={goToToday}>Today</Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="h-96 flex items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="grid grid-cols-7 gap-px bg-border rounded-lg overflow-hidden">
              {/* Day headers */}
              {DAYS.map(d => (
                <div key={d} className="bg-muted/50 p-2 text-center text-xs font-medium text-muted-foreground">
                  {d}
                </div>
              ))}

              {/* Calendar cells */}
              {calendarCells.map(({ day, key }) => {
                if (!day) {
                  return <div key={key} className="bg-background p-2 min-h-[80px]" />;
                }

                const events = getEventsForDay(day);
                const _hasEvents = events.scheduled.length > 0 || events.audits.length > 0;

                return (
                  <div
                    key={key}
                    onClick={() => setSelectedDay({ day, events })}
                    className={`bg-background p-2 min-h-[80px] cursor-pointer hover:bg-muted/30 transition-colors ${
                      isToday(day) ? 'ring-2 ring-amber-500 ring-inset' : ''
                    }`}
                  >
                    <span className={`text-xs font-medium ${
                      isToday(day) ? 'bg-amber-500 text-white rounded-full w-6 h-6 flex items-center justify-center' : 'text-foreground'
                    }`}>
                      {day}
                    </span>
                    <div className="mt-1 space-y-0.5">
                      {events.scheduled.slice(0, 3).map(s => (
                        <div
                          key={s.id}
                          className={`text-[9px] font-medium text-white px-1 py-0.5 rounded truncate ${
                            STATUS_COLORS[s.status] || STATUS_COLORS.scheduled
                          }`}
                        >
                          {s.audit_programmes?.programme_name || 'Audit'}
                        </div>
                      ))}
                      {events.audits.slice(0, 2).map(a => (
                        <div
                          key={a.id}
                          className={`text-[9px] font-medium text-white px-1 py-0.5 rounded truncate ${
                            STATUS_COLORS[a.status] || STATUS_COLORS.scheduled
                          }`}
                        >
                          {a.audit_title?.slice(0, 20) || 'Audit'}
                        </div>
                      ))}
                      {(events.scheduled.length + events.audits.length > 3) && (
                        <div className="text-[9px] text-muted-foreground">
                          +{events.scheduled.length + events.audits.length - 3} more
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Legend */}
          <div className="flex gap-4 mt-4 text-xs text-muted-foreground flex-wrap">
            {[
              { color: 'bg-blue-500', label: 'Scheduled' },
              { color: 'bg-amber-500', label: 'In Progress' },
              { color: 'bg-green-500', label: 'Completed' },
              { color: 'bg-red-500', label: 'Overdue' },
            ].map(l => (
              <div key={l.label} className="flex items-center gap-1.5">
                <div className={`w-2.5 h-2.5 rounded-sm ${l.color}`} />
                <span>{l.label}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Day Detail Dialog */}
      <Dialog open={!!selectedDay} onOpenChange={o => { if (!o) setSelectedDay(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {selectedDay && `${selectedDay.day} ${MONTHS[month]} ${year}`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {selectedDay?.events?.scheduled?.length === 0 && selectedDay?.events?.audits?.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No events on this day</p>
            ) : (
              <>
                {selectedDay?.events?.scheduled?.map(s => (
                  <div key={s.id} className="flex items-center gap-3 p-3 rounded-lg border">
                    <div className={`w-2 h-8 rounded-full ${STATUS_COLORS[s.status] || STATUS_COLORS.scheduled}`} />
                    <div className="flex-1">
                      <p className="text-sm font-medium">{s.audit_programmes?.programme_name || 'Scheduled Audit'}</p>
                      <p className="text-xs text-muted-foreground capitalize">{s.status}</p>
                    </div>
                  </div>
                ))}
                {selectedDay?.events?.audits?.map(a => (
                  <div key={a.id} className="flex items-center gap-3 p-3 rounded-lg border">
                    <div className={`w-2 h-8 rounded-full ${STATUS_COLORS[a.status] || STATUS_COLORS.scheduled}`} />
                    <div className="flex-1">
                      <p className="text-sm font-medium">{a.audit_title}</p>
                      <p className="text-xs text-muted-foreground capitalize">{a.status}</p>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* New Programme Dialog */}
      <Dialog open={showProgrammeDialog} onOpenChange={o => { if (!o) setShowProgrammeDialog(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Audit Programme</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Programme Name *</Label>
              <Input
                value={progForm.programme_name}
                onChange={e => setProgForm(f => ({ ...f, programme_name: e.target.value }))}
                placeholder="e.g. Monthly Safety Inspection"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Template *</Label>
              <Select value={progForm.template_id} onValueChange={v => setProgForm(f => ({ ...f, template_id: v }))}>
                <SelectTrigger><SelectValue placeholder="Select template..." /></SelectTrigger>
                <SelectContent>
                  {templates.map(t => <SelectItem key={t.id} value={t.id}>{t.template_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Frequency</Label>
              <Select value={progForm.frequency} onValueChange={v => setProgForm(f => ({ ...f, frequency: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="biweekly">Biweekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="biannual">Bi-Annual</SelectItem>
                  <SelectItem value="annual">Annual</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={progForm.start_date}
                  onChange={e => setProgForm(f => ({ ...f, start_date: e.target.value }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={progForm.end_date}
                  onChange={e => setProgForm(f => ({ ...f, end_date: e.target.value }))}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProgrammeDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateProgramme} disabled={savingProg}>
              {savingProg ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              Create Programme
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
